import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
// Import global styles
import './styles/global.css'
import { queryClient } from './modules/queryClient'
import { ClientAnalytics } from './modules/clientAnalytics'
import { resolveInitialLanguage } from '@/modules/i18n'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { initSentry } from '@/instrument.sentry'
import { isClientDevEnv } from '@/client-env'

initSentry()

const reactStrictMode = isClientDevEnv

// 🏴‍☠️ Expose deployment tag on window for debugging and monitoring
declare global {
  interface Window {
    __deploy_tag?: string
  }
}

// Set deployment tag on window if available
if (import.meta.env.VITE_DEPLOYMENT_TAG) {
  window.__deploy_tag = import.meta.env.VITE_DEPLOYMENT_TAG
}

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  scrollRestorationBehavior: 'instant',
  scrollToTopSelectors: ['#main-content'],
  defaultViewTransition: true,
})

ClientAnalytics.init()
ClientAnalytics.setUserProperties({ language: resolveInitialLanguage() })

// Track pageviews on every SPA navigation
if (ClientAnalytics.isInitialized) {
  router.subscribe('onResolved', () => {
    ClientAnalytics.trackPageview()
  })
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// 🐥 April Fools
import { AprilFools } from './modules/aprilFools'
const today = new Date()
const isAprilFirst = today.getMonth() === 3 && today.getDate() === 1
const isAprilFirstDebug = localStorage.getItem('april-fools-debug') === 'true'
if (isAprilFirst || isAprilFirstDebug) {
  new AprilFools()
}

const rootElement = document.getElementById('root')
if (rootElement) {
  const app = (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
  const tree = reactStrictMode ? <React.StrictMode>{app}</React.StrictMode> : app

  // Hydrate when the prerender captured real content into #root; cold-mount
  // otherwise (SPA shell). Structural mismatches inside Suspense boundaries
  // recover by client-rendering that subtree; mismatches at the root fall
  // back to full CSR — i.e. equivalent to createRoot. onRecoverableError
  // surfaces both so we can spot regressions in telemetry.
  // Swallow hydration mismatch reports here so they don't end up in Mixpanel
  // session replays (its rrweb plugin captures console output by default).
  // The router-internal Suspense boundary produces a benign mismatch React
  // patches in place without flicker; suppressing the log is purely noise
  // control.
  const onRecoverableError = () => {}
  if (rootElement.firstElementChild) {
    // The prerendered HTML reflects the fully-loaded route tree. TanStack
    // Router wraps the Outlet in its own Suspense; on a fresh mount that
    // boundary is suspended until matches resolve, which mismatches the
    // resolved DOM. Preloading the current matches before hydrate keeps
    // React's first render structurally aligned with the captured snapshot.
    // A residual dev-only mismatch warning about the router's internal
    // Suspense boundary can still fire on first paint; React patches in
    // place without flicker. See Matches.js / Transitioner.js in
    // @tanstack/react-router for the boundary that produces it.
    router.load().then(() => {
      ReactDOM.hydrateRoot(rootElement, tree, { onRecoverableError })
    })
  } else {
    ReactDOM.createRoot(rootElement).render(tree)
  }
}
